import { useQueryClient } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';

import type { User } from '@/utils/session/fetch-user';

import { queryKeys } from '@/hooks/api/query-keys';
import { DEFAULT_PROFILE_PICTURE_URL } from '@/utils/constants';
import { cx } from '@/utils/cx';

import type { AvatarProps } from '../base/avatar/avatar';

import { Avatar } from '../base/avatar/avatar';
import { AvatarAddButton } from '../base/avatar/base-components';

const styles = {
  sm: { root: 'gap-2', name: 'text-sm font-semibold', userId: 'text-xs' },
  md: { root: 'gap-2', name: 'text-sm font-semibold', userId: 'text-sm' },
  lg: { root: 'gap-3', name: 'text-md font-semibold', userId: 'text-md' },
  xl: { root: 'gap-4', name: 'text-lg font-semibold', userId: 'text-md' },
};

interface DesktopAvatarLabelGroupProps extends AvatarProps {
  size: 'sm' | 'md' | 'lg' | 'xl';
  user?: User | null;
}

export const DesktopAvatarLabelGroup = ({
  className,
  user,
  ...props
}: DesktopAvatarLabelGroupProps) => {
  const queryClient = useQueryClient();
  const router = useRouter();

  return (
    <div className="relative flex items-center gap-3 rounded-xl p-3">
      <figure
        className={cx(
          'group flex min-w-0 flex-1 items-center',
          styles[props.size].root,
          className,
        )}
      >
        {user?.picture && user.picture !== DEFAULT_PROFILE_PICTURE_URL ? (
          <Avatar src={user.picture} {...props} />
        ) : (
          <AvatarAddButton
            size="md"
            userId={user?.id}
            onUploadSuccess={() => {
              queryClient.removeQueries({
                queryKey: queryKeys.session.current(),
              });
              void router.invalidate();
            }}
          />
        )}
        <figcaption className="min-w-0 flex-1">
          <p className={cx('text-primary', styles[props.size].name)}>
            {user?.name}
          </p>
          <p
            className={cx('truncate text-tertiary', styles[props.size].userId)}
          >
            {user?.email}
          </p>
        </figcaption>
      </figure>
    </div>
  );
};
